/**
 * AdminSidebar — Collapsible left sidebar navigation for the Admin Panel
 *
 * Features:
 *  - Data-driven nav config with RBAC (role-based group/item visibility)
 *  - Collapsible to icon-only mode (64px) with hover tooltips
 *  - Groups with expand/collapse chevrons + left-accent active state
 *  - Badge support on items (e.g. open ticket count)
 *  - Mobile: slide-in drawer with backdrop
 *  - Collapse preference persisted to localStorage
 *  - Keyboard: Ctrl+B / Cmd+B toggles collapse
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, CreditCard, UserPlus, BarChart3, GraduationCap, Server,
  Activity, MessageSquare, Headphones, ShieldAlert, FolderGit2,
  Megaphone, Send, Mail, Map, BookOpen, Briefcase, UserCircle,
  TrendingUp, Image, Award, Shield, ChevronDown, LogOut,
  PanelLeftClose, PanelLeftOpen, X, Menu, Wallet, MousePointerClick,
  BadgePercent, ClipboardList,
} from 'lucide-react';

// ── RBAC — mirrors backend ROLE_TABS exactly ──────────────────────────────────
const ROLE_TABS = {
  super_admin:  null,
  jobs_manager: new Set(['jobs']),
  support:      new Set(['users', 'tickets', 'agents']),
  finance:      new Set(['payments', 'revenue', 'enroll']),
  content:      new Set(['courses', 'settings', 'blueprint', 'new_course_guide', 'broadcast', 'email_broadcast']),
  analytics:    new Set(['analytics', 'email_activity', 'activity', 'profile_stats']),
};

// ── Nav config ────────────────────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    id: 'people',
    label: 'People',
    icon: Users,
    defaultOpen: true,
    items: [
      { id: 'users',    label: 'Users',    icon: Users },
      { id: 'payments', label: 'Payments', icon: CreditCard },
      { id: 'enroll',   label: 'Enroll',   icon: UserPlus },
      { id: 'revenue',  label: 'Revenue',  icon: Wallet },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    icon: GraduationCap,
    defaultOpen: false,
    items: [
      { id: 'courses',          label: 'Courses',          icon: GraduationCap },
      { id: 'blueprint',        label: 'Blueprint',        icon: Map },
      { id: 'new_course_guide', label: 'New Course Guide', icon: BookOpen },
      { id: 'settings',         label: 'Announcements',    icon: Megaphone },
    ],
  },
  {
    id: 'emails',
    label: 'Emails & Comms',
    icon: Mail,
    defaultOpen: false,
    items: [
      { id: 'email_broadcast', label: 'Email Blast',     icon: Mail },
      { id: 'broadcast',       label: 'Broadcast',       icon: Send },
      { id: 'email_activity',  label: 'Email Activity',  icon: Activity },
    ],
  },
  {
    id: 'jobs',
    label: 'Jobs & Careers',
    icon: Briefcase,
    defaultOpen: false,
    items: [
      { id: 'jobs',          label: 'Jobs',        icon: Briefcase },
      { id: 'certificates',  label: 'Certificates',icon: Award },
      { id: 'profile_stats', label: 'Profiles',    icon: UserCircle },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: TrendingUp,
    defaultOpen: false,
    items: [
      { id: 'analytics',   label: 'Analytics',    icon: BarChart3 },
      { id: 'activity',    label: 'Activity',     icon: Activity },
      { id: 'performance', label: 'Performance',  icon: TrendingUp },
    ],
  },
  {
    id: 'system',
    label: 'System',
    icon: Server,
    defaultOpen: false,
    items: [
      { id: 'server',         label: 'Server',        icon: Server },
      { id: 'network',        label: 'Network',       icon: Activity },
      { id: 'health_monitor', label: 'Health Monitor',icon: Activity },
      { id: 'security',       label: 'Security',      icon: ShieldAlert },
    ],
  },
  {
    id: 'creative',
    label: 'Creative',
    icon: Image,
    defaultOpen: false,
    items: [
      { id: 'poster',      label: 'Promo Poster', icon: Image },
      { id: 'achievement', label: 'Achievement',  icon: Award },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    icon: Headphones,
    defaultOpen: false,
    items: [
      { id: 'tickets',       label: 'Tickets',  icon: MessageSquare },
      { id: 'agents',        label: 'Agents',   icon: Headphones },
      { id: 'projects_mgmt', label: 'Projects', icon: FolderGit2 },
    ],
  },
  {
    id: 'exams',
    label: 'Exams',
    icon: BadgePercent,
    defaultOpen: false,
    superAdminOnly: true,
    items: [
      { id: 'exam_management', label: 'Exam Management', icon: BadgePercent },
      { id: 'exam_bookings',   label: 'Exam Bookings',   icon: ClipboardList },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: Shield,
    defaultOpen: false,
    superAdminOnly: true,
    items: [
      { id: 'team', label: 'Team', icon: Shield },
    ],
  },
];

// ── NavItem button ────────────────────────────────────────────────────────────
const NavBtn = ({ icon: Icon, label, badge, active, collapsed, small, onClick }) => (
  <button
    onClick={onClick}
    title={collapsed ? label : undefined}
    className={[
      'w-full flex items-center rounded-lg font-medium transition-all duration-150 relative group',
      small   ? 'gap-2.5 px-2.5 py-1.5 text-xs' : 'gap-3 px-3 py-2 text-sm',
      collapsed ? 'justify-center' : '',
      active
        ? 'bg-orange-500/15 text-orange-300 border-l-2 border-orange-500 rounded-l-none pl-[calc(0.625rem+2px)]'
        : 'text-slate-400 hover:bg-slate-800/70 hover:text-slate-200',
    ].join(' ')}
  >
    <Icon className={`shrink-0 ${small ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
    {!collapsed && <span className="flex-1 text-left truncate">{label}</span>}
    {!collapsed && badge > 0 && (
      <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none">
        {badge > 99 ? '99+' : badge}
      </span>
    )}
    {/* Tooltip in collapsed mode */}
    {collapsed && (
      <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-slate-700 border border-slate-600 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity duration-150 shadow-xl">
        {label}
        {badge > 0 && <span className="ml-1.5 text-orange-400">({badge})</span>}
      </div>
    )}
  </button>
);

// ── Main sidebar component ────────────────────────────────────────────────────
export const AdminSidebar = ({
  activeTab,
  onNav,
  onLogout,
  adminRole,
  adminName,
  badges = {},
  collapsed,
  setCollapsed,
  mobileOpen,
  setMobileOpen,
}) => {
  const allowedTabs = ROLE_TABS[adminRole] ?? null; // null = all

  // Which groups are open
  const [openGroups, setOpenGroups] = useState(() =>
    Object.fromEntries(NAV_GROUPS.map(g => [g.id, g.defaultOpen || false]))
  );

  // Auto-open the group containing activeTab on mount
  useEffect(() => {
    NAV_GROUPS.forEach(g => {
      if (g.items.some(i => i.id === activeTab)) {
        setOpenGroups(s => ({ ...s, [g.id]: true }));
      }
    });
  }, [activeTab]);

  // Keyboard shortcut Ctrl+B / Cmd+B
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        setCollapsed(v => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setCollapsed]);

  const toggleGroup = useCallback((id) =>
    setOpenGroups(s => ({ ...s, [id]: !s[id] })), []);

  const handleNav = useCallback((id) => {
    onNav(id);
    setMobileOpen(false);
  }, [onNav, setMobileOpen]);

  // Filter groups/items by RBAC
  const visibleGroups = NAV_GROUPS.filter(g => {
    if (g.superAdminOnly && adminRole !== 'super_admin') return false;
    if (allowedTabs === null) return true;
    return g.items.some(i => allowedTabs.has(i.id));
  });

  const getVisibleItems = (group) => {
    if (allowedTabs === null) return group.items;
    return group.items.filter(i => allowedTabs.has(i.id));
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={[
          'fixed top-0 left-0 h-full flex flex-col z-50',
          'bg-slate-900 border-r border-slate-800/80',
          'transition-[width] duration-200 ease-in-out',
          collapsed ? 'w-[68px]' : 'w-[260px]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          'transition-transform lg:transition-[width] duration-200',
        ].join(' ')}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-4 border-b border-slate-800/80 shrink-0 min-h-[64px]">
          {!collapsed && (
            <div className="min-w-0 flex items-center gap-2.5 flex-1">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/20">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-bold text-sm leading-tight truncate">Admin Panel</p>
                <p className="text-slate-500 text-xs truncate">{adminName}</p>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center mx-auto shadow-lg shadow-amber-500/20">
              <Shield className="w-4 h-4 text-white" />
            </div>
          )}
          {/* Collapse toggle — desktop only */}
          <button
            onClick={() => setCollapsed(v => !v)}
            className={`hidden lg:flex p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition-colors shrink-0 ${collapsed ? 'mt-0 mx-auto' : ''}`}
            title={collapsed ? 'Expand sidebar (Ctrl+B)' : 'Collapse sidebar (Ctrl+B)'}
          >
            {collapsed
              ? <PanelLeftOpen className="w-4 h-4" />
              : <PanelLeftClose className="w-4 h-4" />
            }
          </button>
          {/* Mobile close */}
          <button onClick={() => setMobileOpen(false)}
            className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Role badge */}
        {!collapsed && adminRole !== 'super_admin' && (
          <div className="px-3 py-2 border-b border-slate-800/60">
            <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full capitalize">
              {adminRole.replace('_', ' ')}
            </span>
          </div>
        )}
        {!collapsed && adminRole === 'super_admin' && (
          <div className="px-3 py-2 border-b border-slate-800/60">
            <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">
              Super Admin
            </span>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-slate-800">
          {visibleGroups.map(group => {
            const visibleItems = getVisibleItems(group);
            if (visibleItems.length === 0) return null;

            const isOpen = openGroups[group.id];
            const GroupIcon = group.icon;
            const hasActiveChild = visibleItems.some(i => i.id === activeTab);
            const groupBadge = visibleItems.reduce((s, i) => s + (badges[i.id] || 0), 0);

            // Single item — render directly, no group wrapper
            if (visibleItems.length === 1) {
              const item = visibleItems[0];
              return (
                <NavBtn
                  key={item.id}
                  icon={item.icon}
                  label={item.label}
                  badge={badges[item.id] || 0}
                  active={activeTab === item.id}
                  collapsed={collapsed}
                  onClick={() => handleNav(item.id)}
                />
              );
            }

            return (
              <div key={group.id}>
                {/* Group header */}
                {collapsed ? (
                  <NavBtn
                    icon={GroupIcon}
                    label={group.label}
                    badge={groupBadge}
                    active={hasActiveChild}
                    collapsed={collapsed}
                    onClick={() => handleNav(visibleItems[0].id)}
                  />
                ) : (
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className={[
                      'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors',
                      hasActiveChild ? 'text-slate-200' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50',
                    ].join(' ')}
                  >
                    <GroupIcon className="w-3.5 h-3.5 shrink-0" />
                    <span className="flex-1 text-left">{group.label}</span>
                    {groupBadge > 0 && (
                      <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none">
                        {groupBadge > 99 ? '99+' : groupBadge}
                      </span>
                    )}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                )}

                {/* Items */}
                {!collapsed && isOpen && (
                  <div className="mt-0.5 ml-3.5 border-l border-slate-800 pl-2 space-y-0.5">
                    {visibleItems.map(item => (
                      <NavBtn
                        key={item.id}
                        icon={item.icon}
                        label={item.label}
                        badge={badges[item.id] || 0}
                        active={activeTab === item.id}
                        collapsed={false}
                        small
                        onClick={() => handleNav(item.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer — Logout */}
        <div className="p-2 border-t border-slate-800/80 shrink-0">
          <button
            onClick={onLogout}
            title="Logout"
            className={[
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              'text-slate-400 hover:text-white hover:bg-slate-800',
              collapsed ? 'justify-center' : '',
            ].join(' ')}
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

// ── Mobile hamburger button (rendered in parent) ──────────────────────────────
export const MobileMenuButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="lg:hidden fixed top-4 left-4 z-30 p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white shadow-lg"
  >
    <Menu className="w-5 h-5" />
  </button>
);
